import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, Platform } from 'react-native';
import { useSelector } from 'react-redux';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';

import { RootState } from '../../redux/store';
import { getBrandDetail, getBrandsData } from '../../api/Price';
import { updateReservation } from '../../api/Reservation';
import { getDiscountCodesData, getStoreDetail } from '../../api/Settings';
import { searchAddress, searchUspsAddress, retrieveUspsAddress, generateSessionToken } from '../../api/AllAddress ';
import { reservationMainInfoStyle } from './styles/ReservationMainInfoStyle';
import LabeledTextInput from '../../common/components/bohform/LabeledTextInput';
import { CommonSelectDropdown } from '../../common/components/CommonSelectDropdown/CommonSelectDropdown';
import { msgStr } from '../../common/constants/Message';
import { useAlertModal } from '../../common/hooks';
import { formatDate } from '../../common/utils/DateUtils';
import { renderBOHFormDatePicker } from '../../common/components/bohform';
import { getAddressLabel } from '../../common/utils/Helpers';
import VehicleScheduleEditor from './VehicleScheduleEditor';

interface InputValues {
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  billableDays: number;
  reservationDuration: string;
  discountCode: string;
  customPrice: string;
  referrer: string;
  group: string;
  note: string;
  deliveryAddress: string;
  email: string;
  phone_number: string;
}

const formatTime12h = (timeStr?: string) => {
  if (!timeStr) return '';
  const parts = timeStr.split(':');
  if (parts.length < 2) return '';
  const hh = parseInt(parts[0], 10);
  const mm = parts[1];
  if (isNaN(hh) || mm === undefined) return '';
  const ampm = hh >= 12 ? 'PM' : 'AM';
  let h = hh % 12;
  h = h ? h : 12;
  return `${h}:${mm} ${ampm}`;
};

const ReservationMainInfo = ({ details, setUpdateCount, onRescheduleComplete }) => {
  const { showAlert } = useAlertModal();

  const [inputValues, setInputValues] = useState<InputValues>({
    startDate: formatDate(new Date()),
    endDate: formatDate(new Date()),
    startTime: '',
    endTime: '',
    billableDays: 0,
    reservationDuration: '',
    discountCode: '',
    customPrice: '',
    referrer: '',
    group: '',
    note: '',
    deliveryAddress: '',
    email: '',
    phone_number: '',
  });
  const updateTimer = useRef(null);

  const [discountCodes, setDiscountCodes] = useState([]);
  const [brandDetail, setBrandDetail] = useState<any>({});
  const [brands, setBrands] = useState<Array<any>>([]);

  const profileSettings = useSelector((state: RootState) => state.profileSettings.settings);
  const allowBrandChange = useMemo(() => {
    const setting = profileSettings?.find((s) => s.item_key === 'allow_reservation_brand_change');
    return `${setting?.item_value}`.toLowerCase() === 'true';
  }, [profileSettings]);
  const [searchedAddresses, setSearchedAddresses] = useState<Array<any>>([]);
  const [selectedAddress, selectAddress] = useState(null);
  const [requireUsps, setRequireUsps] = useState(false);
  const [uspsZipCodes, setUspsZipCodes] = useState<string>('');
  const uspsSessionToken = useRef(generateSessionToken());
  const [searchKey, setSearchKey] = useState('');
  const searchTimer = useRef(null);
  const [startTimeFocused, setStartTimeFocused] = useState(false);
  const [endTimeFocused, setEndTimeFocused] = useState(false);

  useEffect(() => {
    if(details){
      const _startDate = formatDate(new Date(`${details.start_date} 00:00:00`))
      const _endDate = formatDate(new Date(`${details.end_date} 00:00:00`))

      const billableDays = Math.ceil((new Date(details.end_date).getTime() - new Date(details.start_date).getTime()) / (1000 * 60 * 60 * 24));

      const totalHours = (new Date(details.end_date).getTime() - new Date(details.start_date).getTime()) / (1000 * 60 * 60);
      const days = Math.floor(totalHours / 24);
      const hours = Math.floor(totalHours % 24);
      const durationText = hours > 0 ? `${days} days and ${hours} hours` : `${days} days`;

      const deliveryAddress = details.use_manual? details.manual_address: (details?.all_addresses?.number??'') + ' ' + (details?.all_addresses?.street??'') + ' ' + (details?.all_addresses?.property_name??'') + ' ' + (details?.all_addresses?.plantation??'');

      setInputValues({
        startDate: _startDate,
        endDate: _endDate,
        startTime: details.start_time || '',
        endTime: details.end_time || '',
        billableDays: billableDays,
        reservationDuration: durationText,
        discountCode: details.promo_code || '',
        customPrice: details.customPrice || '',
        referrer: details.referrer || '',
        group: details.group || '',
        deliveryAddress: deliveryAddress,
        note: details.note,
        email: details.customer?.email || '',
        phone_number: details.customer?.phone_number || '',
      });

      if(details.use_manual) setSearchKey(details?.manual_address??'');
      else if(details.all_addresses) setSearchKey(`${details.all_addresses.number || ''} ${details.all_addresses.street || ''} ${details.all_addresses.property_name? `${details.all_addresses.property_name}` :''}, ${details.all_addresses.plantation || ''}`);
    }
  }, [details]);

  useEffect(()=>{
    if(details && details.brand_id){
      getBrandDetail({id:details.brand_id}, (jsonRes, status)=>{
        if(status == 200) setBrandDetail(jsonRes);
        else setBrandDetail({});
      });
    } else setBrandDetail({});
  }, [(details && details.brand_id)])

  useEffect(() => {
    if (inputValues.startDate && inputValues.endDate) {
      const _startDate = new Date(inputValues.startDate) || new Date();
      const _endDate = new Date(inputValues.endDate) || new Date();
  
      const calculateBillableDaysAndDuration = () => {
        const billableDays = Math.ceil((_endDate.getTime() - _startDate.getTime()) / (1000 * 60 * 60 * 24));

        const totalHours = (_endDate.getTime() - _startDate.getTime()) / (1000 * 60 * 60);
        const days = Math.floor(totalHours / 24);
        const hours = Math.floor(totalHours % 24);
        const durationText = hours > 0 ? `${days} days and ${hours} hours` : `${days} days`;

        setInputValues(prev => ({
          ...prev,
          billableDays: billableDays,
          reservationDuration: durationText,
        }));
      };
  
      calculateBillableDaysAndDuration();
    }
  }, [inputValues.startDate, inputValues.endDate]);

  useEffect(()=>{
    getDiscountCodesData((jsonRes, status, error) => {
      switch (status) {
        case 200:
          setDiscountCodes(jsonRes);
          break;
      }
    });
  }, [])

  useEffect(()=>{
    if(allowBrandChange){
      getBrandsData((jsonRes, status)=>{
        if(status == 200 && Array.isArray(jsonRes)) setBrands(jsonRes);
        else setBrands([]);
      });
    }
  }, [allowBrandChange])

  const brandsDropdownData = useMemo(() => {
    if (!brands?.length) return [];
    return brands.map((item, index) => ({
      value: item,
      displayLabel: item.brand,
      index,
    }));
  }, [brands]);

  const defaultBrand = useMemo(() => {
    if (details && details.brand_id)
      return brandsDropdownData.find((item) => item.value.id == details.brand_id) || null;
    return null;
  }, [details, brandsDropdownData]);

  const handleBrandChange = (brandId) => {
    const payload: any = {
      id: details.id,
      brand_id: brandId,
    };

    updateReservation(payload, (jsonRes, status) => {
      switch (status) {
        case 201:
          getBrandDetail({ id: brandId }, (bRes, bStatus) => {
            if (bStatus == 200) setBrandDetail(bRes);
          });
          setUpdateCount(prev => prev + 1);
          break;
        default:
          if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
          else showAlert('error', msgStr('unknownError'));
          break;
      }
    });
  };

  useEffect(() => {
    const bId = details?.brand_id;
    if (!bId) { setRequireUsps(false); return; }
    getStoreDetail(bId, (jsonRes:any, status:number) => {
      setRequireUsps(status === 200 && !!jsonRes?.require_usps_address);
      setUspsZipCodes(jsonRes?.usps_zip_codes || '');
    });
  }, [details?.brand_id]);

  const getAddresses = useCallback(async (q:any) => {
    selectAddress(null);
    
    // Always search even with single character
    const filterToken = q.toLowerCase().trim();

    // USPS Address Lock-in: when the brand requires it, search the live
    // verified-address typeahead (Mapbox) instead of the existing address book.
    if (requireUsps) {
      try {
        const uspsRes = await searchUspsAddress(filterToken, undefined, uspsZipCodes || undefined, uspsSessionToken.current);
        const suggestions = await uspsRes.json();
        setSearchedAddresses(Array.isArray(suggestions)
          ? suggestions.map((a:any) => ({
              label: a.full_address,
              full_address: a.full_address,
              mapbox_id: a.mapbox_id,
              latitude: a.latitude,
              longitude: a.longitude,
              __usps: true,
            }))
          : []);
      } catch {
        setSearchedAddresses([]);
      }
      return;
    }

    const response = await searchAddress(filterToken, details.brand_id);
    const items = await response.json();
    if (Array.isArray(items)) {
      let formatedOptions = items.map((address, index) => {
        const label = getAddressLabel(address, index)
        if (label === '') return null;
        const addressOption = { ...address, label: label }
        return addressOption;
      })

      formatedOptions = formatedOptions.filter((item)=>item)
      setSearchedAddresses(formatedOptions);
    } else {
      setSearchedAddresses([]);
    }
  }, [details, requireUsps])

  const discountCodesDropdownData = useMemo(() => {
    if (!discountCodes?.length) {
      return [];
    }

    const result: Array<any> = discountCodes.map((item, index) => {
      return {
        value: item,
        displayLabel: item.code,
        index,
      };
    });
    return result;
  }, [discountCodes]);

  const defaultDiscountCode = useMemo(()=>{
    if(details && details.promo_code)
      return discountCodesDropdownData.find(item=>item.value.id == details.promo_code);
    else return null;
  }, [details, discountCodes]);
  
  const handleInputChange = (fieldName, value) => {
    const newValues = {
      ...inputValues,
      [fieldName]: value
    };

    const payload:any = {
      id: details.id,
    }

    if(newValues.startDate) payload.start_date = newValues.startDate;
    if(newValues.endDate) payload.end_date = newValues.endDate;
    if(fieldName === 'startTime') payload.start_time = newValues.startTime || null;
    if(fieldName === 'endTime') payload.end_time = newValues.endTime || null;
    if(newValues.discountCode) payload.promo_code = newValues.discountCode;
    if(newValues.deliveryAddress) payload.delivery_address = newValues.deliveryAddress

    // if(fieldName == 'discountCode'){
    //   const discountInfo = discountCodes.find((item) => item.id === newValues.discountCode);
    //   const discountAmount = discountInfo.type == 1 ? (Math.round(details.subtotal * discountInfo.amount) / 100) : discountInfo.amount;
    //   const taxAmount = (details.subtotal - discountAmount + (details.driver_tip || 0)) * (details.tax_rate?details.tax_rate/100:0) ?? 0;
    //   payload.promo_code = newValues.discountCode;
    //   payload.discount_amount = discountAmount;
    //   payload.tax_amount = taxAmount;
    //   payload.total_price = details.subtotal + details.tax_amount + (details.driver_tip || 0) - discountAmount;
    // }

    updateReservation(payload, (jsonRes, status) => {
      switch (status) {
        case 201:
          setInputValues(newValues);
          setUpdateCount(prev => prev + 1);
          break;
        default:
          if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
          else showAlert('error', msgStr('unknownError'));
          break;
      }
    })
  };

  const handleInputChange2 = (fieldName: string, value: any) => {
    const newValues = {
      ...inputValues,
      [fieldName]: value
    };

    setInputValues(newValues);

    const payload: any = {
      id: details.id,
      [fieldName]: value,
    };

    if (updateTimer.current) {
      clearTimeout(updateTimer.current);
    }

    updateTimer.current = setTimeout(() => {
      updateReservation(payload, (jsonRes, status) => {
        switch (status) {
          case 201:
            break;
        }
      });
    }, 1000);
  };

  const handleAddressChange = async (event, value) => {
    // USPS Address Lock-in: a verified pick has no all_addresses row — persist
    // it as a manual (standardized) address plus the verified cross-reference.
    // Suggestions from the Search Box API don't include lat/lng, so resolve
    // them via retrieveUspsAddress before persisting.
    if (value?.__usps || value?.mapbox_id) {
      let full_address = value.full_address;
      let latitude = value.latitude;
      let longitude = value.longitude;

      if (latitude == null || longitude == null) {
        try {
          const retrieveRes = await retrieveUspsAddress(value.mapbox_id, undefined, uspsSessionToken.current);
          const resolved = await retrieveRes.json();
          if (resolved && resolved.mapbox_id) {
            full_address = resolved.full_address || full_address;
            latitude = resolved.latitude;
            longitude = resolved.longitude;
          }
        } catch {
          // fall through with whatever we have; updateReservation still
          // persists the verified mapbox_id/address even without GPS.
        }
        // Start a fresh session for the next search (billing groups suggest+retrieve).
        uspsSessionToken.current = generateSessionToken();
      }

      const uspsPayload: any = {
        id: details.id,
        use_manual: true,
        manual_address: full_address,
        mapbox_id: value.mapbox_id,
        usps_address: full_address,
        lat: latitude,
        lng: longitude,
        manually_updated: true,
        is_address_synced: false,
      };
      updateReservation(uspsPayload, (jsonRes, status) => {
        switch (status) {
          case 201:
            selectAddress({ ...value, full_address, latitude, longitude });
            setSearchKey(full_address);
            break;
          default:
            if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
            else showAlert('error', msgStr('unknownError'));
            break;
        }
      });
      return;
    }

    const payload: any = {
      id: details.id,
      address_id : value.id,
      use_manual : false,
    }

    if(value.id != details.address_id){
      payload.manually_updated = true;
      payload.is_address_synced = false;
    }

    updateReservation(payload, (jsonRes, status) => {
      switch (status) {
        case 201:
          selectAddress(value);
          break;
        default:
          if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
          else showAlert('error', msgStr('unknownError'));
          break;
      }
    })
  }

  return (
    <View style={{marginRight:30, zIndex:10}}>
      <View style={[styles.reservationRow]}>
        <View style={{marginRight:30}}>
          <Text style={{marginBottom:6, color:"#555555"}}>{'Order number'}</Text>
          <Text style={styles.text} selectable={true}>{details?.order_number??' '}</Text>
        </View>
        <View style={{zIndex:20}}>
          {allowBrandChange ? (
            <CommonSelectDropdown
              width={200}
              height={40}
              onItemSelected={(item) => {
                handleBrandChange(item.value.id);
              }}
              data={brandsDropdownData}
              placeholder="Select A Brand"
              title={'Brand'}
              titleStyle={{marginBottom:6, color:"#555555", fontSize:14}}
              defaultValue={defaultBrand}
            />
          ) : (
            <>
              <Text style={{marginBottom:6, color:"#555555"}}>{'Brand'}</Text>
              <Text style={[styles.text]} selectable={true}>{brandDetail?.brand??' '}</Text>
            </>
          )}
        </View>
      </View>
      <View style={[styles.reservationRow]}>
        <View style={{flex:1, padding:1}}>
          <Text style={{marginBottom:6, color:"#555555"}}>{'Customer'}</Text>
          <Text style={[styles.text, {width:'100%'}]} selectable={true}>
            {(details?.customer?.first_name??'') + ' ' + (details?.customer?.last_name ?? '')}
          </Text>
        </View>
      </View>
      <View style={[styles.reservationRow, {zIndex:10}]}>
        <LabeledTextInput
          label='Customer Email'
          width={200}
          containerStyle={{marginRight:30}}
          placeholder='Customer Email'
          placeholderTextColor="#ccc"
          inputStyle={{marginVertical:6}}
          value={inputValues.email}
          onChangeText={value => handleInputChange2('email', value)}
        />
        <LabeledTextInput
          label='Customer Phone'
          width={200}
          placeholder='Customer Phone'
          placeholderTextColor="#ccc"
          inputStyle={{marginVertical:6}}
          value={inputValues.phone_number}
          onChangeText={value => handleInputChange2('phone_number', value)}
        />
      </View>
      {(details?.pricing_system === true || details?.pricing_system === 1) ? (
        <View style={[styles.reservationRow, {zIndex:10}]}>
          <VehicleScheduleEditor details={details} setUpdateCount={setUpdateCount} />
        </View>
      ) : (
      <View style={[styles.reservationRow, {zIndex:10}]}>
        <View style={{width:200, marginRight:30}}>
          <Text style={{marginBottom:6, color:"#555555"}}>{'Start date'}</Text>
          {Platform.OS == 'web' && 
            renderBOHFormDatePicker(inputValues.startDate, (date) => {
              handleInputChange('startDate', formatDate(date));
          })}
          {!!details?.start_time && (
            <View>
              <LabeledTextInput
                label='Start time'
                width={'100%'}
                containerStyle={{marginTop:6}}
                placeholder='HH:MM (24h)'
                placeholderTextColor="#ccc"
                inputStyle={{marginVertical:6}}
                value={startTimeFocused ? inputValues.startTime : (formatTime12h(inputValues.startTime) || inputValues.startTime)}
                onFocus={() => setStartTimeFocused(true)}
                onChangeText={(value) => setInputValues(prev => ({ ...prev, startTime: value }))}
                onBlur={() => {
                  setStartTimeFocused(false);
                  handleInputChange('startTime', inputValues.startTime);
                }}
              />
              <Text style={{color:'#888', fontSize:11, marginTop:-6, marginBottom:6, fontStyle:'italic'}}>
                {'Use 24-hour format (e.g. 14:30)'}
              </Text>
            </View>
          )}
        </View>
        <View style={{width:200}}>
          <Text style={{marginBottom:6, color:"#555555"}}>{'End date'}</Text>
          {Platform.OS == 'web' && 
            renderBOHFormDatePicker(inputValues.endDate, (date) => {
              handleInputChange('endDate', formatDate(date));
          })}
          {!!details?.end_time && (
            <View>
              <LabeledTextInput
                label='End time'
                width={'100%'}
                containerStyle={{marginTop:6}}
                placeholder='HH:MM (24h)'
                placeholderTextColor="#ccc"
                inputStyle={{marginVertical:6}}
                value={endTimeFocused ? inputValues.endTime : (formatTime12h(inputValues.endTime) || inputValues.endTime)}
                onFocus={() => setEndTimeFocused(true)}
                onChangeText={(value) => setInputValues(prev => ({ ...prev, endTime: value }))}
                onBlur={() => {
                  setEndTimeFocused(false);
                  handleInputChange('endTime', inputValues.endTime);
                }}
              />
              <Text style={{color:'#888', fontSize:11, marginTop:-6, marginBottom:6, fontStyle:'italic'}}>
                {'Use 24-hour format (e.g. 14:30)'}
              </Text>
            </View>
          )}
        </View>
      </View>
      )}
      {/* <View style={styles.reservationRow}>
        <LabeledTextInput
          label='Billable days'
          width={200}
          containerStyle={{marginRight:30}}
          placeholder='Billable days'
          placeholderTextColor="#ccc"
          inputStyle={{marginVertical:6}}
          value={inputValues.billableDays.toString()}
          onChangeText={value => handleInputChange('billableDays', value)}
          editable={false}
        />
        <LabeledTextInput
          label='Reservation duration'
          width={200}
          placeholder='Reservation duration'
          placeholderTextColor="#ccc"
          inputStyle={{marginVertical:6}}
          value={inputValues.reservationDuration}
          onChangeText={value => handleInputChange('reservationDuration', value)}
          editable={false}
        />
      </View> */}
      <View style={styles.reservationRow}>
        <CommonSelectDropdown
          containerStyle={{
            marginRight: 30,
          }}
          width={200}
          height={40}
          onItemSelected={(item) => {
            handleInputChange('discountCode', item.value.id);
          }}
          data={discountCodesDropdownData}
          placeholder="Select A Code"
          title={'Discount code'}
          titleStyle={{marginBottom:6, color:"#555555", fontSize:14}}
          defaultValue={defaultDiscountCode}
        />
        <View>
          <Text style={{marginBottom:6, color:"#555555"}}>{'Discount Rate'}</Text>
          <Text style={styles.text} selectable={true}>{(discountCodes && details && details.promo_code)?discountCodes.find(item=>item.id==details.promo_code)?.amount??' ':' '}</Text>
        </View>
      </View>
      <View style={[styles.reservationRow, {width:'100%'}]}>
        {Platform.OS === 'web' ?
          <>
            <Text style={{marginBottom:6, color:"#555555"}}>{'Delivery Address'}</Text>
            <Autocomplete
              // Multiple={true}
              freeSolo
              sx={{ 
                width: '100%', 
                padding: '0px',
                borderRadius: '0px',
              }}
              disableClearable
              options={searchedAddresses}
              value={selectedAddress}
              inputValue={searchKey}
              onChange={handleAddressChange}
              onInputChange={(event, value)=>{
                setSearchKey(value);
                if (searchTimer.current) {
                  clearTimeout(searchTimer.current);
                }
                
                searchTimer.current = setTimeout(() => {
                  getAddresses(value);
                }, 500);
              }}
              onClose = {(event: React.SyntheticEvent, reason: string)=>{
                
              }}
              filterOptions={(x) => {
                return x;
              }}
              renderInput={(params) => {
                const text = String(params?.inputProps?.value ?? ' ');
                const spanIndex = text.indexOf('<span');
                const inputVal = spanIndex !== -1 ? text.substring(0, spanIndex) : text;
                return (
                  <TextField
                    {...params}
                    inputProps={{
                      ...params.inputProps,
                      type: 'search',
                      value: inputVal,
                      style: {
                        padding:'2px',
                        fontSize:'14px',
                      }
                    }}
                  />
                )
              }}
              renderOption={(props, option) => {
                const htmlLabel = { __html: option.label };
                return (
                  <li {...props}>
                    <span style={{fontSize:'14px'}} dangerouslySetInnerHTML={htmlLabel} />
                  </li>
                );
              }}
            />
          </>
          :
          <LabeledTextInput
            label='Delivery Address'
            width={"100%"}
            placeholder='Delivery Address'
            placeholderTextColor="#ccc"
            inputStyle={{marginVertical:6}}
            editable={false}
            value={inputValues.deliveryAddress}
            onChangeText={value => handleInputChange('group', value)}
          />}
      </View>

      <View style={[styles.reservationRow]}>
        <LabeledTextInput
          label='Notes'
          width={"100%"}
          placeholder='Notes'
          placeholderTextColor="#ccc"
          multiline={true}
          inputStyle={{height:100}}
          value={inputValues.note}
          onChangeText={value => handleInputChange2('note', value)}
        />
      </View>
    </View>
  );
};

const styles = reservationMainInfoStyle;

export default ReservationMainInfo;
